/**
 * SIGN IN WITH MEGACITY — the same door MU uses (mu-app's mcoAuth): the NEXUS Vault's Sngine sign-in opens as a popup on
 * megacity.online; when the person authorizes, the vault posts the bearer back to this page (this origin is on the vault's bridge
 * allowlist). The bearer is checked with the vault (/oauth/sngine/me) and kept in this site's storage until it expires or is refused.
 */
const VAULT_BASE = (import.meta.env.VITE_VAULT_BASE as string | undefined) ?? 'https://vault.psyopsys.com'
const TOKEN_KEY = 'npc_mco_token', EXPIRES_KEY = 'npc_mco_token_expires'

export interface Identity { subject: string; idp?: string; displayName?: string; email?: string }
export type Status = 'unauth' | 'pending' | 'authed' | 'expired'

export const auth = $state<{ status: Status; token: string | null; identity: Identity | null; error: string | null }>({
  status: 'unauth', token: null, identity: null, error: null,
})

const store = {
  get: (k: string) => { try { return localStorage.getItem(k) } catch { return null } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v) } catch { /* private mode: this tab only */ } },
  del: (k: string) => { try { localStorage.removeItem(k) } catch { /* nothing kept */ } },
}

export function startAuth(): void {
  window.addEventListener('message', onBridge)
  const t = store.get(TOKEN_KEY), exp = store.get(EXPIRES_KEY)
  if (t && (!exp || new Date(exp).getTime() > Date.now())) { auth.status = 'pending'; void verify(t) }
}

export function signIn(): void {
  const popup = window.open(`${VAULT_BASE}/oauth/sngine/login`, 'nexus-vault-auth', 'width=520,height=720,popup')
  if (!popup) { auth.error = 'The sign-in window was blocked — allow pop-ups for this site and try again.'; return }
  auth.status = 'pending'; auth.error = null
  const stop = Date.now() + 5 * 60_000
  const tick = setInterval(() => {
    if (auth.status === 'authed') clearInterval(tick)
    else if (Date.now() > stop || popup.closed) {
      clearInterval(tick)
      if (auth.status === 'pending') { auth.status = 'unauth'; auth.error = popup.closed ? null : 'Sign-in timed out.' }
    }
  }, 400)
}

export function signOut(): void {
  store.del(TOKEN_KEY); store.del(EXPIRES_KEY)
  Object.assign(auth, { status: 'unauth', token: null, identity: null, error: null })
}

/** a mu-api call refused the bearer mid-session: drop it, ask again */
export function bearerRefused(): void { if (auth.token) { signOut(); auth.status = 'expired' } }

function onBridge(ev: MessageEvent): void {
  if (ev.origin !== VAULT_BASE) return
  const d = ev.data
  if (!d || d.type !== 'mco-auth' || typeof d.token !== 'string') return
  store.set(TOKEN_KEY, d.token)
  if (d.expiresAt) store.set(EXPIRES_KEY, d.expiresAt)
  void verify(d.token, d.identity ?? null)
}

async function verify(token: string, identity: Identity | null = null): Promise<void> {
  try {
    const r = await fetch(`${VAULT_BASE}/oauth/sngine/me`, { headers: { Authorization: `Bearer ${token}` } })
    if (r.status === 401) { signOut(); auth.status = 'expired'; return }
    if (!r.ok) throw new Error(`the vault answered ${r.status}`)
    const me = await r.json() as { identity?: Identity }
    Object.assign(auth, { token, identity: me.identity ?? identity, status: 'authed', error: null })
  } catch (e) {
    auth.status = 'unauth'; auth.error = `Could not reach the vault: ${(e as Error).message}`
  }
}
